/**
 * FlowDiagram Component - Visual representation of the system flow
 *
 * Simplified and cleaner version for developer experience
 */

import React from 'react';
import { CheckCircle2, XCircle, Clock, GitBranch } from 'lucide-react';
import type { FlowDiagramProps } from './types/stats.types';

export const FlowDiagram: React.FC<FlowDiagramProps> = ({ aiFlowTrace, theme }) => {
  if (!aiFlowTrace || aiFlowTrace.length === 0) return null;

  const formatDuration = (ms: number): string => {
    if (!Number.isFinite(ms)) return '-';
    if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
    return `${Math.round(ms)}ms`;
  };

  const getStageLabel = (stageName: string): string => {
    const labels: Record<string, string> = {
      'query_classification': 'Clasificar Query',
      'document_selection': 'Seleccionar Docs',
      'strategy_selection': 'Seleccionar Estrategia',
      'strategy_execution': 'Ejecutar Estrategia',
      'rag_hybrid_retrieval': 'Búsqueda Híbrida',
      'rag_mmr': 'Filtro MMR',
      'rag_reranking': 'Reranking',
      'rag_add_neighbors': 'Agregar Vecinos',
      'rag_llm_completion': 'Generar Respuesta',
      'map_reduce': 'Map-Reduce',
    };
    return labels[stageName] || stageName.replace(/_/g, ' ');
  };

  const getStageIcon = (status: string) => {
    if (status === 'success') {
      return <CheckCircle2 className="w-3 h-3 text-green-400 flex-shrink-0" />;
    }
    if (status === 'error') {
      return <XCircle className="w-3 h-3 text-red-400 flex-shrink-0" />;
    }
    return <Clock className="w-3 h-3 text-yellow-400 flex-shrink-0" />;
  };

  const getStageDetails = (data: any): string[] => {
    if (!data) return [];
    const details: string[] = [];

    if (data.query_type) details.push(`Tipo: ${data.query_type}`);
    if (data.strategy) details.push(`${data.strategy.replace('Strategy', '')}`);
    if (data.method) details.push(`${data.method}`);
    if (data.documents_count !== undefined) details.push(`${data.documents_count} docs`);
    if (data.input_size?.chunks) details.push(`In: ${data.input_size.chunks}`);
    if (data.output_size?.chunks) details.push(`Out: ${data.output_size.chunks}`);

    return details;
  };

  // Calculate total time
  const totalTime = aiFlowTrace.reduce((sum, stage) => sum + (stage.time_ms || 0), 0);

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className={`flex items-center gap-1.5 ${theme.colors.text.primary} text-[10px] font-medium`}>
          <GitBranch className="w-3 h-3 text-purple-400" />
          <span>Flujo del Sistema</span>
        </div>
        <span className={`text-[9px] ${theme.colors.text.secondary}`}>
          Total: {formatDuration(totalTime)}
        </span>
      </div>

      {/* Flow Steps */}
      <div className="space-y-1">
        {aiFlowTrace.map((stage, idx) => {
          const details = getStageDetails(stage.data);
          const percentage = totalTime > 0 ? ((stage.time_ms || 0) / totalTime) * 100 : 0;

          return (
            <div
              key={idx}
              className={`flex items-start gap-2 text-[9px] py-1 px-2 rounded ${theme.colors.bg.primary}/30`}
            >
              {/* Icon */}
              {getStageIcon(stage.status)}

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className={`${theme.colors.text.primary} font-medium truncate`}>
                    {getStageLabel(stage.stage)}
                  </span>
                  <span className={`${theme.colors.text.secondary} flex-shrink-0`}>
                    {formatDuration(stage.time_ms)}
                  </span>
                </div>

                {/* Details */}
                {details.length > 0 && (
                  <div className={`flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5 ${theme.colors.text.secondary}/70`}>
                    {details.map((detail, i) => (
                      <span key={i}>{detail}</span>
                    ))}
                  </div>
                )}

                {/* Time bar */}
                {percentage > 0 && (
                  <div className={`mt-1 h-0.5 rounded-full ${theme.colors.bg.secondary}`}>
                    <div
                      className="h-full rounded-full bg-purple-400/50"
                      style={{ width: `${Math.max(percentage, 2)}%` }}
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
